import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ZegoUIKitPrebuilt } from '@zegocloud/zego-uikit-prebuilt';
import { Alert, Container, Button } from 'react-bootstrap';

/**
 * Test Meeting Room
 *
 * PURPOSE: Direct entry point for test meetings via URL parameters.
 * Accepts ZegoCloud token and room details via query params and joins immediately.
 *
 * URL FORMAT:
 * /test-meeting?roomID=xxx&token=xxx&userName=xxx&userID=xxx&role=host|guest
 *
 * FEATURES:
 * - No backend API calls required
 * - Immediate room join with provided token
 * - Role indicator (host/guest badge)
 * - Console error monitoring
 * - Network quality tracking
 * - Clean exit back to generator
 */
export default function TestMeetingRoom() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const roomContainer = useRef();
  const zegoInstanceRef = useRef(null);

  const roomID = searchParams.get('roomID');
  const token = searchParams.get('token');
  const userName = searchParams.get('userName');
  const userID = searchParams.get('userID');
  const role = searchParams.get('role');

  useEffect(() => {
    // Validate required parameters
    if (!roomID || !token || !userName || !userID) {
      setError('Missing required parameters. Please use the Test Meeting Generator to create a valid meeting link.');
      return;
    }

    // Store in localStorage (matching normal flow)
    localStorage.setItem('ZEGO_TOKEN', token);
    localStorage.setItem('demoId', roomID);
    localStorage.setItem('username', userName);

    // Mark as coming from waiting room to avoid redirect
    sessionStorage.setItem('cameFromWaitingRoom', 'true');

    // Initialize ZegoCloud
    try {
      console.log('🚀 Initializing ZegoCloud Test Meeting:', {
        roomID,
        userID,
        userName,
        role,
        timestamp: new Date().toISOString()
      });

      const zegoInstance = ZegoUIKitPrebuilt.create(token);
      zegoInstanceRef.current = zegoInstance;

      zegoInstance.joinRoom({
        container: roomContainer.current,
        scenario: {
          mode: ZegoUIKitPrebuilt.VideoConference,
        },
        showPreJoinView: false,
        turnOnMicrophoneWhenJoining: true,
        turnOnCameraWhenJoining: true,
        showScreenSharingButton: true,
        showLeavingView: false,
        showroomtimer: true,
        autoHideFooter: false,
        branding: {
          logoURL: "https://purebrand-storage.s3.us-east-2.amazonaws.com/logo1.png"
        },
        screenSharingConfig: {
          resolution: "auto",
          width: 1280,
          height: 720,
          frameRate: 15,
          maxBitRate: 1500,
          onError: (errorCode) => {
            console.error('🚨 Screen sharing error:', errorCode);
            if (errorCode === 1001) return 'Screen sharing failed due to permission issues';
            if (errorCode === 1002) return 'Screen sharing stopped unexpectedly';
            return '';
          }
        },
        leaveRoomDialogConfig: {
          confirmCallback: () => {
            console.log('👋 Leaving test meeting');
            localStorage.removeItem('ZEGO_TOKEN');
            localStorage.removeItem('demoId');
            localStorage.removeItem('username');
            sessionStorage.removeItem('cameFromWaitingRoom');
            navigate('/test-meeting-generator');
          }
        },
        onJoinRoom: () => {
          console.log('✅ Successfully joined test meeting room:', roomID);
          setConnected(true);
        },
        onLeaveRoom: () => {
          console.log('👋 Left test meeting room');
          setConnected(false);
        },
        onUserJoin: (user) => {
          console.log('✅ User joined:', {
            userID: user.userID,
            userName: user.userName,
            timestamp: new Date().toISOString()
          });
        },
        onUserLeave: (user) => {
          console.log('👋 User left:', {
            userID: user.userID,
            userName: user.userName,
            timestamp: new Date().toISOString()
          });
        },
        onError: (errorCode) => {
          console.error('🚨 ZegoCloud error:', errorCode);
        }
      });

      // Monitor network quality
      if (zegoInstance.on) {
        zegoInstance.on('networkQuality', (userID, upstreamQuality, downstreamQuality) => {
          console.log('📊 Network Quality:', {
            userID,
            upstream: upstreamQuality, // 0=excellent, 3=poor, 5=down
            downstream: downstreamQuality,
            timestamp: new Date().toISOString()
          });

          if (upstreamQuality >= 3 || downstreamQuality >= 3) {
            console.warn('⚠️ Poor network quality detected!');
          }
        });

        zegoInstance.on('roomStateUpdate', (roomID, state, errorCode, extendedData) => {
          console.log('🔄 Room State Update:', {
            roomID,
            state, // DISCONNECTED, CONNECTING, CONNECTED
            errorCode,
            extendedData,
            timestamp: new Date().toISOString()
          });

          if (errorCode !== 0) {
            console.error('🚨 Room state error:', {
              errorCode,
              state,
              extendedData
            });
          }
        });
      }

    } catch (err) {
      console.error('❌ Error initializing ZegoCloud:', err);
      setError(`Failed to initialize meeting: ${err.message}`);
    }

    // Cleanup on unmount
    return () => {
      if (zegoInstanceRef.current) {
        console.log('🧹 Cleaning up ZegoCloud instance');
        try {
          zegoInstanceRef.current.destroy();
        } catch (err) {
          console.error('Error destroying ZegoCloud instance:', err);
        }
      }
    };
  }, [roomID, token, userName, userID, role, navigate]);

  if (error) {
    return (
      <Container className="mt-5" style={{ maxWidth: '600px' }}>
        <Alert variant="danger">
          <h4>❌ Error Loading Test Meeting</h4>
          <p>{error}</p>
          <hr />
          <div className="d-flex gap-2">
            <Button
              variant="primary"
              onClick={() => navigate('/test-meeting-generator')}
            >
              ← Back to Generator
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => window.location.reload()}
            >
              🔄 Retry
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100vw', height: '100vh' }}>
      {/* Role indicator badge */}
      <div style={{
        position: 'absolute',
        top: 10,
        left: 10,
        zIndex: 1000,
        background: role === 'host' ? '#28a745' : '#007bff',
        color: 'white',
        padding: '10px 20px',
        borderRadius: '6px',
        fontSize: '16px',
        fontWeight: 'bold',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <span style={{ fontSize: '20px' }}>
          {role === 'host' ? '🎯' : '👤'}
        </span>
        <span>
          {role === 'host' ? 'TEST HOST' : 'TEST GUEST'}
        </span>
        {connected && (
          <span style={{
            background: '#fff',
            color: '#28a745',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '12px',
            marginLeft: '8px'
          }}>
            ● LIVE
          </span>
        )}
      </div>

      {/* Connection status indicator */}
      {!connected && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 999,
          background: 'rgba(0,0,0,0.8)',
          color: 'white',
          padding: '20px 40px',
          borderRadius: '8px',
          fontSize: '18px'
        }}>
          🔄 Connecting to test meeting...
        </div>
      )}

      {/* ZegoCloud video container */}
      <div
        id="roomContainer"
        ref={roomContainer}
        style={{ width: '100%', height: '100%' }}
      ></div>

      {/* Debug info (only visible in console) */}
      <script dangerouslySetInnerHTML={{__html: `
        console.log('Test Meeting Debug Info:', {
          roomID: '${roomID}',
          userName: '${userName}',
          userID: '${userID}',
          role: '${role}',
          timestamp: new Date().toISOString()
        });
      `}} />
    </div>
  );
}
